import React, { useEffect, useState } from "react";
import { API, companyIdentity } from "@/lib/api";

// Website (Marketstack profile) + logo (downloaded by our backend from the company's own site
// and served from our domain: no Google/third-party logo service). Null while loading or if
// the company has neither.
export function useCompanyIdentity(ticker) {
    const [identity, setIdentity] = useState(null);
    useEffect(() => {
        setIdentity(null);
        if (!ticker) return undefined;
        let alive = true;
        companyIdentity(ticker).then((d) => { if (alive) setIdentity(d); }).catch(() => { /* initials, no link */ });
        return () => { alive = false; };
    }, [ticker]);
    return identity;
}

// Company logo; without one, the ticker initials on brand blue. `className` sets the size
// (e.g. "w-14 h-14", or "w-[0.9em] h-[0.9em]" to match the surrounding text) and
// `initialsClass` the size of the initials (on the inner span, so an em-sized box keeps its size).
// A logo smaller than MIN_LOGO_PX (some sites only publish a 16 px favicon) would look
// pixelated, so the initials are shown instead, on the company's colour when it has one.
const MIN_LOGO_PX = 32;

export function CompanyLogo({ logoUrl, logoWidth, brandColor, ticker, className = "w-11 h-11 sm:w-14 sm:h-14", initialsClass = "text-lg sm:text-xl", testid = "company-logo" }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [logoUrl]);
    const box = `${className} shrink-0 border border-[#052049]/20 flex items-center justify-center overflow-hidden`;
    const tiny = !!logoWidth && logoWidth < MIN_LOGO_PX;
    if (!logoUrl || failed || tiny) {
        const bg = companyColorProps(brandColor).style;
        const own = /^#[0-9a-f]{6}$/i.test(brandColor || "");
        return (
            <span className={`${box} ${own ? "" : "bg-[#052049]"} text-white font-serif font-semibold leading-none`}
                style={own ? { backgroundColor: bg["--co"] } : undefined} aria-hidden data-testid={`${testid}-fallback`}>
                <span className={initialsClass}>{(ticker || "?").replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase()}</span>
            </span>
        );
    }
    return (
        // No percentage padding here: it resolves against the parent's width (not the box's),
        // which blew the box up and squeezed the image to nothing in the company header.
        <span className={`${box} bg-white`}>
            <img src={`${API}${logoUrl}`} alt="" className="w-[84%] h-[84%] object-contain"
                onError={() => setFailed(true)} data-testid={testid} />
        </span>
    );
}

// "palantir.com" from "https://www.palantir.com/".
export const siteLabel = (url) => (url || "").replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

// ---- Company brand colour (dominant colour of its logo, computed by the backend) ----
const NAVY = { light: "#052049", dark: "#3B9EFF" };   // ours, when the logo has no clear colour

const hexToRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const rgbToHex = (rgb) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
const luminance = (rgb) => {
    const [r, g, b] = rgb.map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a, b) => { const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };
// Mix towards black (light theme) or white (dark theme) until the text reads (WCAG 4.5:1).
const readable = (rgb, bg, towards) => {
    let c = rgb;
    for (let t = 0; t <= 1 && contrast(c, bg) < 4.5; t += 0.05) c = rgb.map((v, i) => v + (towards[i] - v) * t);
    return rgbToHex(c);
};

// Props for an element coloured with the company's colour: className + CSS variables for
// both themes (see .company-color in index.css). Falls back to our navy.
export function companyColorProps(hex) {
    if (!/^#[0-9a-f]{6}$/i.test(hex || "")) return { className: "company-color", style: { "--co": NAVY.light, "--co-dark": NAVY.dark } };
    const rgb = hexToRgb(hex);
    return {
        className: "company-color",
        style: {
            "--co": readable(rgb, [255, 255, 255], [0, 0, 0]),       // header card is white
            "--co-dark": readable(rgb, [28, 24, 19], [255, 255, 255]), // --bg-surface in dark mode
        },
    };
}
